import React, { useMemo } from 'react';
import { AccessorKeyColumnDef, createColumnHelper, RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { getDisplayName } from '../../../../utils/person';
import Table from './Table';
import { DeleteCell, InviteCell, VerifiedCell } from '../TableCells';
import { useUser } from '../../../../hooks/use-user';

export type UserDataEntry = {
	id: number;
	firstName: string;
	lastName: string;
	emailAddress: string;
	phoneNumber: string;
	role: string;
	companyContactPersonUser: {
		verified: boolean;
		roles: string[];
	};
};

const columnHelper = createColumnHelper<UserDataEntry>();

type Props = {
	users: UserDataEntry[];
	handleDelete: (row: RowData) => void;
	handleRowClick: (row: RowData) => void;
	handleInvite: (row: RowData) => void;
};

const CompanyContactPersonOverview = ({
	users,
	handleInvite,
	handleDelete,
	handleRowClick,
}: Props) => {
	const { profile } = useUser();
	const { t } = useTranslation();

	const columns = useMemo(
		() =>
			[
				columnHelper.display({
					id: 'name',
					cell: (cellInfo) => getDisplayName(cellInfo.row.original),
					header: t('dation_instance.name'),
				}),
				columnHelper.accessor('emailAddress', {
					header: t('portal.email_placeholder'),
				}),
				columnHelper.accessor('companyContactPersonUser.roles', {
					header: t('users.role'),
					cell: (cellInfo) =>
						(cellInfo.row.original.companyContactPersonUser?.roles.length || 0) > 0
							? t(
									`users.roles.${cellInfo.row.original.companyContactPersonUser.roles[0]?.toLowerCase()}`,
								)
							: '',
				}),
				columnHelper.accessor('companyContactPersonUser.verified', {
					header: t('add_employee.verified'),
					cell: (cellInfo) => <VerifiedCell verified={cellInfo.cell.getValue()} />,
				}),
				columnHelper.display({
					id: 'invite',
					header: t('users.invite'),
					cell: (cellInfo) => (
						<InviteCell
							handleInvite={handleInvite}
							verified={cellInfo.row.original.companyContactPersonUser?.verified}
							original={cellInfo.row.original}
							buttonText={
								cellInfo.row.original.companyContactPersonUser
									? t('users.resend_invite')
									: t('users.send_invite')
							}
						/>
					),
				}),
				columnHelper.display({
					id: 'delete',
					cell: (cellInfo) => (
						<DeleteCell
							handleDelete={handleDelete}
							original={cellInfo.row.original}
							isCurrentUser={
								profile.companyContactPerson
									? profile.companyContactPerson.id === cellInfo.row.original.id
									: false
							}
						/>
					),
				}),
			] as Array<AccessorKeyColumnDef<UserDataEntry, unknown>>,
		[],
	);

	return <Table data={users} columns={columns} handleRowClick={handleRowClick} />;
};

export default CompanyContactPersonOverview;
